/**
 * Read-only transcript window around a cited turn. Desktop: right drawer;
 * phone: bottom sheet. Renders an "unavailable" state when no transcript is
 * wired (the mentor-scoped transcript endpoint may not exist yet).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import type { SessionTranscript } from '@/types/mentorEngagements';
import { mentorStyles } from '../shared';
import { transcriptWindow } from './model';

const WINDOW = 6;
const PAGE = 12;

interface Props {
  visible: boolean;
  transcript: SessionTranscript | undefined;
  centerTurn: number | null;
  phone: boolean;
  onClose: () => void;
  onCopyTimestamp?: (timestamp: string) => void;
}

export function TranscriptSideSheet({ visible, transcript, centerTurn, phone, onClose, onCopyTimestamp }: Props) {
  const { t } = useTranslation();
  const [before, setBefore] = useState(WINDOW);
  const [after, setAfter] = useState(WINDOW);
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState(false);
  useEffect(() => { setBefore(WINDOW); setAfter(WINDOW); setQuery(''); setCopied(false); }, [centerTurn, visible]);

  const turns = useMemo(() => transcriptWindow(transcript, centerTurn ?? 0, before, after), [transcript, centerTurn, before, after]);
  const needle = query.trim().toLowerCase();
  const filtered = needle ? turns.filter(turn => turn.text.toLowerCase().includes(needle)) : turns;
  const total = transcript?.total_turns ?? transcript?.turns.length ?? 0;
  const canEarlier = Boolean(transcript) && (centerTurn ?? 0) - before > 0;
  const canLater = Boolean(transcript) && (centerTurn ?? 0) + after < total - 1;

  return (
    <Modal visible={visible} transparent animationType={phone ? 'slide' : 'fade'} onRequestClose={onClose}>
      <View style={[styles.backdrop, phone ? styles.backdropPhone : styles.backdropDesktop]}>
        <Pressable accessibilityRole="button" accessibilityLabel={t('mentorEngagements.review.transcript.close')} onPress={onClose} style={styles.scrim} />
        <View testID="sof_transcript_sheet" style={[styles.sheet, phone ? styles.sheetPhone : styles.sheetDesktop]}>
          <View style={styles.head}>
            <View style={{ flex: 1, gap: 2 }}>
              <Text style={mentorStyles.panelTitle}>{t('mentorEngagements.review.transcript.title')}</Text>
              {centerTurn != null && transcript ? <Text style={mentorStyles.mutedText}>{t('mentorEngagements.review.transcript.around', { turn: centerTurn })}</Text> : null}
            </View>
            <Pressable accessibilityRole="button" accessibilityLabel={t('mentorEngagements.review.transcript.close')} onPress={onClose} style={({ pressed }) => [{ padding: 4 }, pressed && mentorStyles.pressed]}>
              <MaterialIcons name="close" size={22} color={reviewDesk.inkSoft} />
            </Pressable>
          </View>

          {!transcript ? (
            <View style={styles.unavailable}>
              <MaterialIcons name="subtitles-off" size={26} color={reviewDesk.muted} />
              <Text style={styles.unavailableText}>{t('mentorEngagements.review.transcript.unavailable')}</Text>
            </View>
          ) : (
            <>
              <View style={styles.search}>
                <MaterialIcons name="search" size={18} color={reviewDesk.muted} />
                <TextInput
                  accessibilityLabel={t('mentorEngagements.review.transcript.search')}
                  placeholder={t('mentorEngagements.review.transcript.search')}
                  placeholderTextColor={reviewDesk.muted}
                  value={query}
                  onChangeText={setQuery}
                  style={styles.searchInput}
                />
              </View>
              {copied ? <Text style={styles.copied}>{t('mentorEngagements.review.transcript.copied')}</Text> : null}
              <ScrollView contentContainerStyle={styles.turns}>
                {canEarlier ? <PagerButton label={t('mentorEngagements.review.transcript.earlier')} onPress={() => setBefore(current => current + PAGE)} /> : null}
                {filtered.map(turn => {
                  const cited = turn.turn_index === centerTurn;
                  const coach = turn.speaker === 'coach';
                  return (
                    <View key={turn.turn_index} testID={`sof_transcript_turn_${turn.turn_index}`} style={[styles.turn, cited && styles.turnCited]}>
                      <View style={styles.turnMeta}>
                        <Text style={[styles.turnSpeaker, !coach && { color: reviewDesk.muted }]}>{t(`mentorEngagements.review.${coach ? 'coach' : 'client'}`)}</Text>
                        <Text style={styles.turnTime}>{turn.timestamp || t('mentorEngagements.review.turn', { number: turn.turn_index })}</Text>
                        {turn.timestamp && onCopyTimestamp ? (
                          <Pressable accessibilityRole="button" accessibilityLabel={t('mentorEngagements.review.transcript.copyTimestamp')}
                            onPress={() => { onCopyTimestamp(turn.timestamp!); setCopied(true); }} style={({ pressed }) => [pressed && mentorStyles.pressed]}>
                            <MaterialIcons name="content-copy" size={14} color={reviewDesk.accent} />
                          </Pressable>
                        ) : null}
                      </View>
                      <Text style={[styles.turnText, !coach && { color: reviewDesk.inkSoft }]}>{turn.text}</Text>
                    </View>
                  );
                })}
                {needle && !filtered.length ? <Text style={mentorStyles.mutedText}>{t('mentorEngagements.review.transcript.noMatches')}</Text> : null}
                {canLater ? <PagerButton label={t('mentorEngagements.review.transcript.later')} onPress={() => setAfter(current => current + PAGE)} /> : null}
              </ScrollView>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
}

function PagerButton({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <Pressable accessibilityRole="button" onPress={onPress} style={({ pressed }) => [styles.pager, pressed && mentorStyles.pressed]}>
      <Text style={styles.pagerText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(16, 24, 39, 0.35)' },
  backdropDesktop: { flexDirection: 'row', justifyContent: 'flex-end' },
  backdropPhone: { justifyContent: 'flex-end' },
  scrim: { ...StyleSheet.absoluteFillObject },
  sheet: { backgroundColor: reviewDesk.card, borderColor: reviewDesk.line, padding: 18, gap: 12 },
  sheetDesktop: { width: 420, maxWidth: '100%', height: '100%', borderLeftWidth: 1 },
  sheetPhone: { height: '85%', borderTopLeftRadius: 20, borderTopRightRadius: 20, borderTopWidth: 1 },
  head: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
  unavailable: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 10, padding: 24 },
  unavailableText: { color: reviewDesk.inkSoft, fontSize: 14, textAlign: 'center', lineHeight: 21 },
  search: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 12, borderRadius: 10, borderWidth: 1, borderColor: reviewDesk.line, backgroundColor: reviewDesk.cardMuted, minHeight: 40 },
  searchInput: { flex: 1, fontSize: 16, color: reviewDesk.ink, paddingVertical: 8 },
  copied: { color: reviewDesk.success, fontSize: 12, fontWeight: '700' },
  turns: { gap: 10, paddingBottom: 24 },
  turn: { gap: 4, padding: 10, borderRadius: 10, borderLeftWidth: 3, borderLeftColor: 'transparent' },
  turnCited: { backgroundColor: reviewDesk.accentWash, borderLeftColor: reviewDesk.accent },
  turnMeta: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  turnSpeaker: { color: reviewDesk.ink, fontSize: 11, fontWeight: '800' },
  turnTime: { color: reviewDesk.muted, fontSize: 11, fontWeight: '600', fontVariant: ['tabular-nums'] },
  turnText: { color: reviewDesk.ink, fontSize: 14, lineHeight: 21 },
  pager: { alignSelf: 'center', minHeight: 34, justifyContent: 'center', paddingHorizontal: 14, borderRadius: 999, borderWidth: 1, borderColor: reviewDesk.line },
  pagerText: { color: reviewDesk.accent, fontSize: 12, fontWeight: '700' },
});
